import * as _ from 'lodash';
import {HistoryLogDialogData, TimecardOption} from '../../types/timekeeping-dialog.type';
import {StopLocation} from '../../types/timecard-detail-view.type';
import {Address} from '../../../addresses/models/address.model';
import {TimeCardHistoryItem} from '../models/timecard-history.model';

const HOUR_MS = 3600000;
const MINUTE_MS = 60000;

export type TimecardHistoryValueType = 'time' | 'duration' | 'distance' | 'location' | 'text';

export interface TimecardHistoryRow {
  timestamp: number;
  stopName: string;
  updatedBy: string;
  userType: string;
  stopIndex: number | string;
  field: string;
  entryId?: string;
  valueType: TimecardHistoryValueType;
  previousState: any;
  currentState: any;
  note: string;
}

export interface DurationParts {
  hours: number;
  minutes: number;
}

const TOP_LEVEL_HISTORY_FIELDS = [
  'status', 'absenceReason', 'adminTime',
  'clockInTime', 'clockOutTime', 'clockInLocation', 'clockOutLocation',
  'clockInTimeField', 'clockOutTimeField', 'clockInLocationField', 'clockOutLocationField'
];

const ENTRY_HISTORY_FIELDS = [
  'startTime', 'endTime', 'serviceTime', 'duration', 'location',
  'drivingTimeFromPrevStop', 'drivingDistanceFromPrevStop'
];

const TIME_FIELDS = ['startTime', 'endTime', 'clockInTime', 'clockOutTime', 'clockInTimeField', 'clockOutTimeField'];
const DURATION_FIELDS = ['serviceTime', 'duration', 'drivingTimeFromPrevStop', 'adminTime'];
const LOCATION_FIELDS = ['clockInLocation', 'clockOutLocation', 'clockInLocationField', 'clockOutLocationField'];

// ******************************
//        Durations
// ******************************

export function toNonNegInt(value: unknown): number {
  if (value === '' || _.isNil(value)) {
    return 0;
  }
  const n = Math.floor(Number(value));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
}

/** Minutes above 59 are capped rather than carried into the hours. */
export function durationToMillis(hours: unknown, minutes: unknown): number {
  return (toNonNegInt(hours) * HOUR_MS) + (Math.min(59, toNonNegInt(minutes)) * MINUTE_MS);
}

export function splitDuration(millis: number): DurationParts {
  const totalMinutes = Math.floor((millis || 0) / MINUTE_MS);
  return {hours: Math.floor(totalMinutes / 60), minutes: totalMinutes % 60};
}

// ******************************
//        Validation
// ******************************

/** Translation key of the first broken rule, or null. A timecard not clocked out yet ends now. */
export function validateStopTimes(arrival: number, departure: number, clockIn: number, clockOut: number): string | null {
  const now = Date.now();
  if (arrival) {
    if (clockIn && arrival < clockIn) {
      return 'ARRIVAL_TIME_CANNOT_BE_BEFORE_CLOCK_IN';
    }
    if (clockOut && arrival > clockOut) {
      return 'ARRIVAL_TIME_CANNOT_BE_AFTER_CLOCK_OUT';
    }
    if (!clockOut && arrival > now) {
      return 'ARRIVAL_TIME_CANNOT_BE_IN_THE_FUTURE';
    }
  }
  if (departure) {
    if (clockIn && departure < clockIn) {
      return 'DEPARTURE_TIME_CANNOT_BE_BEFORE_CLOCK_IN';
    }
    if (clockOut && departure > clockOut) {
      return 'DEPARTURE_TIME_CANNOT_BE_AFTER_CLOCK_OUT';
    }
    if (!clockOut && departure > now) {
      return 'DEPARTURE_TIME_CANNOT_BE_IN_THE_FUTURE';
    }
  }
  if (arrival && departure && departure <= arrival) {
    return 'DEPARTURE_TIME_MUST_BE_AFTER_ARRIVAL';
  }
  return null;
}

/** A break may not overlap any active stop of the timecard - task, location or another break. */
export function validateBreakOverlap(start: number, end: number, timecard: TimecardOption): string | null {
  if (!start || !end || !timecard) {
    return null;
  }
  const overlaps = _.some(timecard.entries, (entry) =>
    !entry.archive && !!entry.startTime && !!entry.endTime && start < entry.endTime && end > entry.startTime);
  return overlaps ? 'BREAK_TIME_OVERLAPS_WITH_EXISTING_STOP' : null;
}

export function isDuplicateBreakType(breakTypeId: string, timecard: TimecardOption): boolean {
  return !!breakTypeId && _.some(timecard?.breaks, {breakTypeId});
}

export const EMPTY_STOP_LOCATION: StopLocation = {lat: 0, lng: 0, address: null, formattedAddress: ''};

/**
 * Stop location of an account or agent address. The API sends the point as `location.lat/lng`
 * (the Address model types it as latitude/longitude), so the address is read untyped, as before.
 */
export function buildStopLocation(address: any, formattedAddress?: string): StopLocation {
  if (!address?.location) {
    return EMPTY_STOP_LOCATION;
  }
  return {
    lat: address.location.lat,
    lng: address.location.lng,
    address,
    formattedAddress: formattedAddress || buildFormattedAddress(address)
  };
}

/**
 * Stop location of an address picked from the Google suggestions. The picker gives the point as
 * latitude/longitude; the timecard keeps it as lat/lng, inside the address as well.
 */
export function buildCustomStopLocation(address: Address): StopLocation {
  if (!address?.location) {
    return EMPTY_STOP_LOCATION;
  }
  const lat = address.location.latitude;
  const lng = address.location.longitude;
  return buildStopLocation({...address, location: {lat, lng}}, address.place);
}

export function buildFormattedAddress(address: any): string {
  if (!address) {
    return '';
  }
  const parts = _.compact([address.house, address.street, address.city, address.state, address.country]);
  return parts.join(', ') || `${address.location?.lat?.toFixed(4)}, ${address.location?.lng?.toFixed(4)}`;
}

// ******************************
//        History log
// ******************************

/**
 * Flattens the paged history into one row per field change, newest first, and narrows it to the
 * stop and field the dialog was opened for.
 */
export function buildHistoryRows(data: HistoryLogDialogData): TimecardHistoryRow[] {
  const isSensitive = !!data.isSensitiveLocationEntry;
  const rows = _.flatMap(data.historyData || [], (item) => flattenHistory(item, isSensitive));
  return filterHistoryRows(sortNewestFirst(rows), data);
}

function flattenHistory(historyData: TimeCardHistoryItem, isSensitive: boolean): TimecardHistoryRow[] {
  if (!historyData) {
    return [];
  }

  const topLevelRows = _.flatMap(TOP_LEVEL_HISTORY_FIELDS, (fieldName) =>
    buildFieldRows(historyData[fieldName], fieldName, isSensitive, {stopName: '-', stopIndex: '-'}));

  const entryRows = _.flatMap(historyData.reportedEntries || [], (entry: any, entryIndex: number) => {
    const stopIndex = entryIndex + 1;
    const stopName = entry.accountReference?.name || entry.locationSource?.name || `Stop ${stopIndex}`;
    const context = {stopName, stopIndex, entryId: entry.id};
    return _.flatMap(ENTRY_HISTORY_FIELDS, (fieldName) => buildFieldRows(entry[fieldName], fieldName, isSensitive, context));
  });

  return topLevelRows.concat(entryRows);
}

function buildFieldRows(fieldData: any, fieldName: string, isSensitive: boolean,
                        context: Pick<TimecardHistoryRow, 'stopName' | 'stopIndex' | 'entryId'>): TimecardHistoryRow[] {
  if (!fieldData?.changes) {
    return [];
  }
  const valueType = getValueType(fieldName);
  let previousValue = fieldData.initialValue;
  return fieldData.changes.map((change: any) => {
    const row: TimecardHistoryRow = {
      ...context,
      timestamp: toMillis(change.updatedAt),
      updatedBy: change.updatedBy || '-',
      userType: change.source === 'Agent' || change.source === 'Employee' ? 'Employee' : 'Admin',
      field: fieldName,
      valueType,
      previousState: normalizeValue(fieldName, previousValue, isSensitive),
      currentState: normalizeValue(fieldName, change.newValue, isSensitive),
      note: change.note || ''
    };
    previousValue = change.newValue;
    return row;
  });
}

function filterHistoryRows(rows: TimecardHistoryRow[], data: HistoryLogDialogData): TimecardHistoryRow[] {
  let filtered = rows;
  if (data.filterEntryId) {
    // The caller knows the stop's displayed name and number, which the history does not
    filtered = filtered
      .filter((row) => row.entryId === data.filterEntryId)
      .map((row) => ({
        ...row,
        stopName: data.filterStopName || row.stopName,
        stopIndex: data.filterEntryIndex || row.stopIndex
      }));
  }
  if (data.filterField) {
    filtered = filtered.filter((row) => row.field === data.filterField);
  }
  return filtered;
}

function getValueType(fieldName: string): TimecardHistoryValueType {
  if (_.includes(TIME_FIELDS, fieldName)) return 'time';
  if (_.includes(DURATION_FIELDS, fieldName)) return 'duration';
  if (fieldName === 'drivingDistanceFromPrevStop') return 'distance';
  if (_.includes(LOCATION_FIELDS, fieldName)) return 'location';
  return 'text';
}

/** Turns a raw history value into what the column's pipe expects. */
function normalizeValue(fieldName: string, value: any, isSensitive: boolean): any {
  if (_.isNil(value)) return null;
  if (_.isString(value)) return value;

  const valueType = getValueType(fieldName);
  if (valueType === 'time') {
    return toMillis(value);
  }
  if (fieldName === 'serviceTime' && _.isObject(value)) {
    return (value as any).generatedTotalTime || 0;
  }
  if ((valueType === 'location' || fieldName === 'location') && _.isObject(value)) {
    const location = value as any;
    if (isSensitive) {
      return buildGeneralizedLocationDisplay(location) || '---';
    }
    return location.formattedAddress || location.address?.formattedAddress ||
      (location.lat && location.lng ? `${location.lat.toFixed(4)}, ${location.lng.toFixed(4)}` : '-');
  }
  return value;
}

/** City, state and zip only - a sensitive location must not reveal the street. */
function buildGeneralizedLocationDisplay(location: any): string {
  const address = location.address || location;
  return _.compact([address.city, address.state, address.zipCode]).join(', ');
}

/** History timestamps come in seconds or millis. */
function toMillis(value: number): number {
  return value > 1e12 ? value : value * 1000;
}

function sortNewestFirst(rows: TimecardHistoryRow[]): TimecardHistoryRow[] {
  return [...rows].sort((a, b) => b.timestamp > a.timestamp ? 1 : -1);
}
